import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Select,
  MenuItem,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const TransactionReport = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Branch
  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(1);

  // Dropdown data
  const [ddlClassType, setDdlClassType] = useState([]);
  const [ddlClass, setDdlClass] = useState([]);
  const [ddlSection, setDdlSection] = useState([]);
  const [ddlStudent, setDdlStudent] = useState([]);

  // Filter state
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [classTypeId, setClassTypeId] = useState(0);

  // "All" toggles — mirrors isAllClass / isAllSection / isAllStudent in the API body
  const [isAllClass, setIsAllClass] = useState(true);
  const [isAllSection, setIsAllSection] = useState(true);
  const [isAllStudent, setIsAllStudent] = useState(true);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");

  // Table & loading
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

const downloadReport = useCallback(() => {
  if (reportData.length === 0) return;

  const headers = [
    "Receipt No", "Student Name", "Adm. No", "Roll No", "Class", "Section",
    "Session", "Payment Date", "Month", "Paid Amount", "Balance",
    "Concession", "Discount", "Fine", "Cancel Date", "Cancel Reason", "Payment Mode", "Status",
  ];

  const rows = reportData.map((item) => [
    item.reciptNo || "-",
    item.studentName || "-",
    item.admissionNo || "-",
    item.rollNo || "-",
    item.className || "-",
    item.sectionName || "-",
    item.session || "-",
    item.paymentDate || "-",
    item.monthName || "-",
    `Rs. ${(item.paidAmount || 0).toFixed(2)}`,
    `Rs. ${(item.balanceAmount || 0).toFixed(2)}`,
    `Rs. ${(item.concession || 0).toFixed(2)}`,
    `Rs. ${(item.discountAmount || 0).toFixed(2)}`,
    `Rs. ${(item.fine || 0).toFixed(2)}`,
    item.cancelDate || "-",
    item.cancelRemark || "-",
    item.paymentMode || "-",
    item.transactionStatus || "-",
  ]);

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  doc.setFontSize(14);
  doc.text("Transaction Report", 40, 30);
  doc.setFontSize(9);
  doc.text(`From: ${fromDate}   To: ${toDate}`, 40, 46);

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 58,
    styles: { fontSize: 6.5, cellPadding: 3 },
    headStyles: { fillColor: [33, 150, 243], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    didParseCell: (data) => {
      const rowItem = reportData[data.row.index];
      if (rowItem && (rowItem.isCancel === 1 || rowItem.transactionStatus === "Cancelled")) {
        data.cell.styles.textColor = [198, 40, 40];
      }
    },
  });

  doc.save(`TransactionReport_${fromDate}_to_${toDate}.pdf`);
}, [reportData, fromDate, toDate]);

  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0];
  };
  const getCurrentDate = () => new Date().toISOString().split("T")[0];

  useEffect(() => {
    setFromDate(getFirstDayOfMonth());
    setToDate(getCurrentDate());
  }, []);

  // ── Branch ──
  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        setBranches(await response.json());
      } else {
        console.error("Failed to fetch branches");
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  useEffect(() => {
    const isMasterUser = sessionStorage.getItem("is_Master") === "true";
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      fetchBranches();
    } else {
      const branchId = sessionStorage.getItem("branchId");
      setSelectedBranchId(branchId);
    }
  }, []);

  // ── Dropdown fetches ──
  // NOTE: confirm this endpoint/shape against your actual Class Type API
  const fetchDdlClassType = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/ClassType/GetClassType`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      const data = await res.json();
      if (data.data === null && data.msg === "Record Not Found") return;
      setDdlClassType(Array.isArray(data) ? data : data.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchDdlClass = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      const data = await res.json();
      if (data.data === null && data.msg === "Record Not Found") return;
      setDdlClass(data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchDdlSection = async (classId) => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/Exam/ddlSection_clsId`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ classId, teacherId: 0 }),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      const data = await res.json();
      setDdlSection(data?.data === null ? [] : data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchDdlStudent = async (classId, sectionId) => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/FeeReport/ddlStudentbyClassId`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ classId, sectionId }),
      });
      if (!res.ok) throw new Error(`Error: ${res.status}`);
      const data = await res.json();
      setDdlStudent(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchDdlClassType();
    fetchDdlClass();
  }, []);

  useEffect(() => {
    if (!isAllClass && selectedClass !== "") {
      fetchDdlSection(selectedClass);
      setSelectedSection("");
      setSelectedStudent("");
      setDdlStudent([]);
    } else {
      setDdlSection([]);
      setSelectedSection("");
      setSelectedStudent("");
      setDdlStudent([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, isAllClass]);

  useEffect(() => {
    if (!isAllSection && selectedSection !== "") {
      fetchDdlStudent(selectedClass, selectedSection);
      setSelectedStudent("");
    } else {
      setDdlStudent([]);
      setSelectedStudent("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection, isAllSection]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const buildRequestBody = useCallback(
    () => ({
      isAllClass,
      isAllSection,
      isAllStudent,
      classId: !isAllClass && selectedClass ? parseInt(selectedClass) : 0,
      sectionId: !isAllSection && selectedSection ? parseInt(selectedSection) : 0,
      studentId: !isAllStudent && selectedStudent ? parseInt(selectedStudent) : 0,
      classTypeId: classTypeId ? parseInt(classTypeId) : 0,
      fromDate,
      toDate,
    }),
    [isAllClass, isAllSection, isAllStudent, selectedClass, selectedSection, selectedStudent, classTypeId, fromDate, toDate]
  );

  const generateReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/FeeReport/ReportFeePaymentTransaction?Branch_id=${branchId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify(buildRequestBody()),
        }
      );

      if (!res.ok) {
        setSnackbarMessage("Error: Please provide proper input!");
        setOpenSnackbar(true);
        return;
      }

      const data = await res.json();

      const isEmpty =
        !data ||
        data.data === null ||
        (Array.isArray(data.data) && data.data.length === 0) ||
        (Array.isArray(data) && data.length === 0);

      if (isEmpty) {
        setSnackbarMessage(data?.msg || "No records found!");
        setOpenSnackbar(true);
        setReportData([]);
        return;
      }

      setReportData(Array.isArray(data) ? data : data.data || []);
    } catch (e) {
      setSnackbarMessage("An error occurred while fetching report data!");
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  }, [buildRequestBody, isMaster, selectedBranchId]);

  // ── Styles (reused from FeeDiscountApprovalReport) ──
  const selectStyles = {
    width: "100%",
    borderRadius: "10px",
    background: "#ffffff",
    boxShadow: "0 2px 6px rgba(0,0,0,0.07)",
    transition: "box-shadow 0.2s ease",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#2196F3", borderWidth: "1.5px" },
    "&:hover": {
      boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1976D2" },
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#21CBF3", borderWidth: "2px" },
    "&.Mui-disabled": { opacity: 0.55 },
    "& .MuiSelect-select": { padding: "11px 16px", fontWeight: 500, color: "#333", fontSize: "0.875rem" },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        borderRadius: "10px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        mt: 0.5,
        "& .MuiMenuItem-root": {
          fontSize: "0.875rem",
          "&:hover": { backgroundColor: "#e3f2fd" },
          "&.Mui-selected": { backgroundColor: "#e3f2fd", "&:hover": { backgroundColor: "#bbdefb" } },
        },
      },
    },
  };

  const fieldLabelSx = {
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#546e7a",
    mb: 0.6,
    letterSpacing: "0.03em",
  };

  return (
    <Box sx={{ padding: "24px", minHeight: "100vh" }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "14px",
          border: "1px solid #e0e0e0",
          p: "22px 24px 20px",
          mb: 3,
          background: "#ffffff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: "18px 24px",
            mb: 2.5,
          }}
        >
          {/* Branch — Master users only */}
          {isMaster && (
            <Box>
              <Typography component="label" sx={fieldLabelSx}>Branch</Typography>
              <Select
                fullWidth
                value={selectedBranchId || ""}
                onChange={(e) => setSelectedBranchId(e.target.value)}
                displayEmpty
                sx={selectStyles}
                MenuProps={menuProps}
              >
                <MenuItem value="" disabled><em>Select Branch</em></MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.branchId} value={branch.branchId}>
                    {branch.branchName}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {/* Class Type */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Class Type</Typography>
            <Select
              fullWidth
              value={classTypeId}
              onChange={(e) => setClassTypeId(e.target.value)}
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value={0}>All Class Types</MenuItem>
              {ddlClassType.map((ct) => (
                <MenuItem key={ct.classTypeId} value={ct.classTypeId}>{ct.classTypeName}</MenuItem>
              ))}
            </Select>
          </Box>

          {/* From Date */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>From Date</Typography>
            <TextField
              type="date"
              fullWidth
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": selectStyles }}
            />
          </Box>

          {/* To Date */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>To Date</Typography>
            <TextField
              type="date"
              fullWidth
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": selectStyles }}
            />
          </Box>

          {/* All Class / Class Wise */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Class</Typography>
            <Select
              fullWidth
              value={isAllClass ? "all" : "specific"}
              onChange={(e) => setIsAllClass(e.target.value === "all")}
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value="all">All Class</MenuItem>
              <MenuItem value="specific">Class Wise</MenuItem>
            </Select>
          </Box>

          {/* Specific Class picker — shown only when Class Wise is selected */}
          {!isAllClass && (
            <Box>
              <Typography component="label" sx={fieldLabelSx}>Select Class</Typography>
              <Select
                fullWidth
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                displayEmpty
                sx={selectStyles}
                MenuProps={menuProps}
              >
                <MenuItem value="" disabled><em>Choose Class</em></MenuItem>
                {ddlClass.map((c) => (
                  <MenuItem key={c.classId} value={c.classId}>{c.className}</MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {/* All Section / Select Section */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Section</Typography>
            <Select
              fullWidth
              value={isAllSection ? "all" : "specific"}
              onChange={(e) => setIsAllSection(e.target.value === "all")}
              disabled={isAllClass}
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value="all">All Section</MenuItem>
              <MenuItem value="specific">Select Section</MenuItem>
            </Select>
          </Box>

          {!isAllSection && (
            <Box>
              <Typography component="label" sx={fieldLabelSx}>Select Section</Typography>
              <Select
                fullWidth
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                displayEmpty
                disabled={!selectedClass}
                sx={selectStyles}
                MenuProps={menuProps}
              >
                <MenuItem value="" disabled><em>Choose Section</em></MenuItem>
                {ddlSection.map((s) => (
                  <MenuItem key={s.sectionId} value={s.sectionId}>{s.sectionName}</MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {/* All Student / Select Student */}
          <Box>
            <Typography component="label" sx={fieldLabelSx}>Student</Typography>
            <Select
              fullWidth
              value={isAllStudent ? "all" : "specific"}
              onChange={(e) => setIsAllStudent(e.target.value === "all")}
              disabled={isAllSection}
              sx={selectStyles}
              MenuProps={menuProps}
            >
              <MenuItem value="all">All Student</MenuItem>
              <MenuItem value="specific">Select Student</MenuItem>
            </Select>
          </Box>

          {!isAllStudent && (
            <Box>
              <Typography component="label" sx={fieldLabelSx}>Select Student</Typography>
              <Select
                fullWidth
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                displayEmpty
                disabled={!selectedSection}
                sx={selectStyles}
                MenuProps={menuProps}
              >
                <MenuItem value="" disabled><em>Choose Student</em></MenuItem>
                {ddlStudent.map((s) => (
                  <MenuItem key={s.studentId} value={s.studentId}>{s.studentName}</MenuItem>
                ))}
              </Select>
            </Box>
          )}
        </Box>

        <Box sx={{ borderTop: "1px solid #f0f0f0", pt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Button
              variant="contained"
              onClick={generateReport}
              disabled={isLoading}
              sx={{
                background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                borderRadius: "25px",
                padding: "9px 28px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minWidth: "160px",
                boxShadow: "0 3px 5px 2px rgba(33,203,243,.3)",
                "&:hover": {
                  background: "linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)",
                },
              }}
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : "Generate Report"}
            </Button>

<Tooltip title="Download PDF Report" arrow>
            <Button
  variant="outlined"
  onClick={downloadReport}
  disabled={reportData.length === 0}
  sx={{
    borderRadius: "25px",
    padding: "9px 28px",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    minWidth: "160px",
    borderColor: "#2196F3",
    color: "#2196F3",
    "&:hover": { borderColor: "#1976D2", backgroundColor: "#e3f2fd" },
  }}
>
  Download Report
</Button></Tooltip>
          </Box>
        </Box>
      </Paper>

    {/* ── Table area ── */}
{isLoading ? (
  <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
    <CircularProgress sx={{ color: "#2196F3" }} />
  </Box>
) : reportData.length > 0 ? (
  <Paper
    elevation={0}
    sx={{ borderRadius: "14px", border: "1px solid #e0e0e0", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
  >
    <TableContainer sx={{ maxHeight: 500, overflow: "auto" }}>
      <Table size="small">
        <TableHead sx={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "#e3f2fd" }}>
          <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
           {[
  "Receipt No", "Student Name", "Adm. No", "Roll No", "Class", "Section",
  "Session", "Payment Date", "Month", "Paid Amount", "Balance",
  "Concession", "Discount", "Fine", "Cancel Date", "Cancel Reason", "Payment Mode", "Status",
].map((h) => (
              <TableCell
                key={h}
                sx={{ fontSize: "0.74rem", fontWeight: 700, color: "#1565c0", whiteSpace: "nowrap", py: 1.5, borderBottom: "2px solid #bbdefb" }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {reportData.map((item, idx) => {
            const isCancelled = item.isCancel === 1 || item.transactionStatus === "Cancelled";
            return (
              <TableRow
                key={item.transationId || idx}
                hover
                sx={{
                  backgroundColor: isCancelled ? "#fff5f5" : idx % 2 === 0 ? "#ffffff" : "#fafafa",
                  "&:hover": { backgroundColor: "#e3f2fd" },
                  "& td": { fontSize: "0.82rem", color: "#333", borderBottom: "1px solid #f0f0f0", py: 1.1 },
                }}
              >
                <TableCell>{item.reciptNo || "-"}</TableCell>
                <TableCell>{item.studentName || "-"}</TableCell>
                <TableCell>{item.admissionNo || "-"}</TableCell>
                <TableCell>{item.rollNo || "-"}</TableCell>
                <TableCell>{item.className || "-"}</TableCell>
                <TableCell>{item.sectionName || "-"}</TableCell>
                <TableCell>{item.session || "-"}</TableCell>
                <TableCell>{item.paymentDate || "-"}</TableCell>
                <TableCell>{item.monthName || "-"}</TableCell>
               <TableCell align="right">₹{(item.paidAmount || 0).toFixed(2)}</TableCell>
<TableCell
  align="right"
  sx={{ color: (item.balanceAmount || 0) > 0 ? "#d32f2f" : "#2e7d32", fontWeight: 600 }}
>
  ₹{(item.balanceAmount || 0).toFixed(2)}
</TableCell>
<TableCell align="right">₹{(item.concession || 0).toFixed(2)}</TableCell>
<TableCell align="right">₹{(item.discountAmount || 0).toFixed(2)}</TableCell>
<TableCell align="right">₹{(item.fine || 0).toFixed(2)}</TableCell>
<TableCell>{item.cancelDate || "-"}</TableCell>
<TableCell>{item.cancelRemark || "-"}</TableCell>
<TableCell>{item.paymentMode || "-"}</TableCell>
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      px: 1,
                      py: 0.3,
                      borderRadius: "12px",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: isCancelled ? "#c62828" : "#2e7d32",
                      backgroundColor: isCancelled ? "#ffebee" : "#e8f5e9",
                    }}
                  >
                    {item.transactionStatus || "-"}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
) : (
  <Box sx={{ textAlign: "center", py: 10 }}>
    <Typography sx={{ fontSize: "2rem", mb: 1 }}>🧾</Typography>
    <Typography variant="h6" sx={{ color: "#555", fontWeight: 600 }}>No Records Found</Typography>
    <Typography sx={{ fontSize: "0.875rem", color: "#999", mt: 0.5 }}>
      Try adjusting your filters to see results
    </Typography>
  </Box>
)}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TransactionReport;